import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import HeroSearch from '../components/HeroSearch';
import ToolkitsAccordion from '../components/ToolkitsAccordion';
import TrustPill from '../components/TrustPill';
import BadgesRow from '../components/BadgesRow';
import Reveal, { RevealStagger } from '../components/Reveal';
import { WebApplicationSchema, HowToSchema } from '../components/StructuredData';
import LeadForm from '../components/LeadForm';
import TestimonialLeadForm from '../components/TestimonialLeadForm';
import TestimonialCard from '../components/TestimonialCard';
import WhyChoose from '../components/WhyChoose';
import { isFeatureEnabled } from '../featureFlags';
import { makeMeta, makeWebsiteJsonLd, makeOrganizationJsonLd } from '../utils/seo';

/**
 * Modern mobile-first homepage following SEMRush layout pattern
 * Features: Hero with search input + accordion toolkits below
 * Includes entrance animations and comprehensive SEO
 */
const NewHomePage = () => {

  // SEO metadata with structured data
  const websiteJsonLd = makeWebsiteJsonLd({
    siteName: 'SalaryTools', 
    url: 'https://salarytools.co.uk'
  });
  
  const organizationJsonLd = makeOrganizationJsonLd({
    name: 'SalaryTools',
    url: 'https://salarytools.co.uk',
    logoUrl: 'https://salarytools.co.uk/logo.png'
  });

  return (
    <Box>
      {makeMeta({
        title: 'See My Take-Home Pay - UK Salary Calculator',
        description: 'Instant, accurate tax breakdown with visual bands. Calculate your UK take-home pay, tax, and National Insurance in seconds. No sign-up required.',
        canonical: 'https://salarytools.co.uk/',
        jsonLd: [websiteJsonLd, organizationJsonLd]
      })}
      
      <WebApplicationSchema />
      <HowToSchema 
        name="How to Calculate Your UK Take Home Pay"
        description="Learn how to calculate your take-home pay using our UK salary calculator"
        steps={[
          {
            name: "Enter your annual salary",
            text: "Input your gross annual salary into the calculator field"
          },
          {
            name: "Add additional details",
            text: "Include pension contributions, student loan repayments, and overtime if applicable"
          },
          {
            name: "View your breakdown",
            text: "See your take-home pay, tax, and National Insurance contributions displayed visually"
          }
        ]}
      />

      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.50',
          minHeight: { xs: '50vh', md: '55vh' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          pb: 4,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 3, sm: 3, md:3 } }}>
          <RevealStagger staggerDelay={80} animation="slideUp">
            {/* Trust Pill - Single neutral trust indicator */}
            <Box sx={{ textAlign: 'center', mb: 4, mt: 4 }}>
              <TrustPill />
            </Box>

            {/* Hero Headline - Concise, outcome-first (≤7 words) */}
            <Box sx={{ textAlign: 'center', mb: 1.5 }}>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 'bold',
                  color: 'text.primary',
                  fontSize: { xs: '2.25rem', md: '3rem', lg: '3.5rem' },
                  lineHeight: { xs: 1.1, md: 1.1 },
                  maxWidth: '600px',
                  margin: '0 auto'
                }}
              >
                Calculate my take-home pay
              </Typography>
            </Box>

            {/* Subhead - No superlatives, factual benefit */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 400,
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  lineHeight: 1.4,
                  maxWidth: '28ch', // ~28-34ch for readability
                  margin: '0 auto'
                }}
              >
                <b>Free</b>, instant, accurate tax breakdown with visual bands.
              </Typography>
            </Box>

            {/* Hero Search */}
            <Box sx={{ mb: 4 }}>
              <HeroSearch onSalaryChange={() => {}} />
            </Box>

            {/* Trust Badges - Factual claims, no superlatives */}
            <Box sx={{ mb: 3 }}>
              <BadgesRow />
            </Box>
          </RevealStagger>
        </Container>
      </Box>

      {/* Toolkits Section */}
      <Box
        sx={{
          bgcolor: '#FEFEFE',
          py: { xs: 6, md: 6 }
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 3, sm: 3, md:3 } }}>
          <Reveal animation="slideUp" delay={200}>
            <ToolkitsAccordion />
          </Reveal>
        </Container>
      </Box>

      {/* Why Choose Section */}
      <Reveal animation="slideUp" delay={300}>
        <WhyChoose />
      </Reveal>

      {/* Testimonial Section */}
      <Reveal animation="slideUp" delay={400}>
        <TestimonialCard
          quote="Finally, a UK calculator that shows me exactly where my tax goes. The visual bands make it much clearer than spreadsheets."
          name="Sarah M."
          role="Financial Planner"
          ratingValue={5.0}
          cta={{ 
            label: "See my take-home pay", 
            to: "/salary-calculator" 
          }}
        />
      </Reveal>

      {/* Lead Forms */}
      {isFeatureEnabled('STANDARD_LEAD_FORM') && (
        <Box sx={{ bgcolor: 'primary.25', py: { xs: 6, md: 8 } }}>
          <Container maxWidth="sm" sx={{ px: { xs: 3, sm: 3, md:3 } }}>
            <Reveal animation="slideUp" delay={100}>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                  Never miss tax changes that affect your pay
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Get notified when rates change + tips to maximize your take-home pay
                </Typography>
              </Box>
              <LeadForm 
                title="Never miss tax updates" 
                subtitle="Join thousands of UK professionals staying informed about salary changes"
              />
            </Reveal>
          </Container>
        </Box>
      )}

      {isFeatureEnabled('TESTIMONIAL_LEAD_FORM') && (
        <Box sx={{ pb: 0 }}>
          <Reveal animation="slideUp" delay={200}>
            <TestimonialLeadForm 
              title="Get pay updates & tips to maximise your take-home pay"
              subtitle="Be first to know about tax changes that affect your salary."
              buttonText="Send me the tips"
              consentText="I agree to receive helpful tips from SalaryTools."
            />
          </Reveal>
        </Box>
      )}

    </Box>
  );
};

export default NewHomePage;